/**
 * Parser für Feuerkennungen (IALA-Kurzschrift) in eine Zeitleiste.
 *
 * "Fl(3)G.10s" wird zu drei Blitzen grün mit Wiederkehr 10 s, "Q(6)+LFl.15s"
 * zu sechs Funkeln plus langem Blitz. Damit ist jede Kennung Daten statt Sonderfall.
 * Die Taktzeiten folgen den IALA-Klassen: Funkel 60/min, schnelles Funkel 120/min,
 * langer Blitz mindestens 2 s.
 */

export type RhythmColour = 'W' | 'R' | 'G' | 'Y' | 'Bu' | 'Vi'

export type RhythmClass =
  | 'F'
  | 'Fl'
  | 'LFl'
  | 'FFl'
  | 'Q'
  | 'VQ'
  | 'UQ'
  | 'IQ'
  | 'IVQ'
  | 'IUQ'
  | 'Iso'
  | 'Oc'
  | 'Mo'
  | 'Al'

export interface Phase {
  /** Dauer in Sekunden. */
  duration: number
  /** 0 = dunkel, 1 = voll, dazwischen = Festfeuer-Grundhelligkeit. */
  intensity: number
  /** Fehlt bei Sektorenfeuern: dann entscheidet die Peilung über die Farbe. */
  colour?: RhythmColour
}

export interface LightCharacter {
  raw: string
  cls: RhythmClass
  /** Gruppenzahlen, z. B. [3] bei Fl(3), [2,1] bei Fl(2+1). */
  groups: number[]
  /** Angehängte Klasse hinter dem Plus, z. B. LFl bei Q(6)+LFl. */
  plus?: 'LFl' | 'Fl' | 'Q'
  colours: RhythmColour[]
  /** Mehrere Farben ohne Al: Sektorenfeuer, Farbe hängt an der Peilung. */
  sectored: boolean
  /** Morsebuchstabe bei Mo(A). */
  morse?: string
  periodS: number
  phases: Phase[]
}

export class RhythmParseError extends Error {}

const TIMING = {
  flashOn: 0.7,
  flashGap: 1.0,
  longFlashOn: 2,
  longFlashGap: 1,
  quickOn: 0.3,
  quickCycle: 1,
  veryQuickOn: 0.15,
  veryQuickCycle: 0.5,
  ultraQuickOn: 0.1,
  ultraQuickCycle: 0.25,
  subGroupGap: 1.2,
  plusGap: 1.2,
  eclipse: 1.5,
  morseDot: 0.5,
  morseDash: 1.5,
  morseGap: 0.5,
  alternateGap: 0.5,
  fixedBaseline: 0.35,
} as const

export const MORSE: Record<string, string> = {
  A: '.-', B: '-...', C: '-.-.', D: '-..', E: '.', F: '..-.', G: '--.', H: '....',
  I: '..', J: '.---', K: '-.-', L: '.-..', M: '--', N: '-.', O: '---', P: '.--.',
  Q: '--.-', R: '.-.', S: '...', T: '-', U: '..-', V: '...-', W: '.--', X: '-..-',
  Y: '-.--', Z: '--..',
}

const CLASS_RE =
  /^(FFl|LFl|Fl|IVQ|IUQ|IQ|VQ|UQ|Q|Iso|Oc|Mo|Al|F)(?:\(([0-9]+(?:\+[0-9]+)*|[A-Z])\))?(?:\+(LFl|Fl|Q))?([A-Za-z]*)$/

const COLOUR_RE = /^(W|R|G|Y|Bu|Vi)+$/

function splitColours(token: string): RhythmColour[] {
  const out: RhythmColour[] = []
  let i = 0
  while (i < token.length) {
    const two = token.slice(i, i + 2)
    if (two === 'Bu' || two === 'Vi') {
      out.push(two)
      i += 2
      continue
    }
    const one = token[i]!
    if (one === 'W' || one === 'R' || one === 'G' || one === 'Y') {
      out.push(one)
      i += 1
      continue
    }
    throw new RhythmParseError(`unbekannte Farbe in "${token}"`)
  }
  return out
}

/** Baut die Phasen einer Gruppe aus n Blitzen der angegebenen Klasse. */
function groupPhases(cls: RhythmClass | 'LFl' | 'Fl' | 'Q', count: number): Phase[] {
  const spec: Record<string, { on: number; off: number }> = {
    Fl: { on: TIMING.flashOn, off: TIMING.flashGap },
    LFl: { on: TIMING.longFlashOn, off: TIMING.longFlashGap },
    Q: { on: TIMING.quickOn, off: TIMING.quickCycle - TIMING.quickOn },
    VQ: { on: TIMING.veryQuickOn, off: TIMING.veryQuickCycle - TIMING.veryQuickOn },
    UQ: { on: TIMING.ultraQuickOn, off: TIMING.ultraQuickCycle - TIMING.ultraQuickOn },
    IQ: { on: TIMING.quickOn, off: TIMING.quickCycle - TIMING.quickOn },
    IVQ: { on: TIMING.veryQuickOn, off: TIMING.veryQuickCycle - TIMING.veryQuickOn },
    IUQ: { on: TIMING.ultraQuickOn, off: TIMING.ultraQuickCycle - TIMING.ultraQuickOn },
  }
  const s = spec[cls]
  if (!s) throw new RhythmParseError(`Klasse ${cls} kennt keine Gruppen`)
  const phases: Phase[] = []
  for (let i = 0; i < count; i += 1) {
    phases.push({ duration: s.on, intensity: 1 })
    phases.push({ duration: s.off, intensity: 0 })
  }
  return phases
}

function total(phases: Phase[]): number {
  return phases.reduce((n, p) => n + p.duration, 0)
}

/**
 * Zerlegt eine Kennung in ihre Zeitleiste.
 * @param raw z. B. "Fl(3)G.10s"
 */
export function parseCharacter(raw: string): LightCharacter {
  const text = raw.trim()
  if (!text) throw new RhythmParseError('leere Kennung')

  let rest = text
  let periodS = 0
  const periodMatch = rest.match(/[.\s]?(\d+(?:\.\d+)?)s$/)
  if (periodMatch) {
    periodS = Number(periodMatch[1])
    rest = rest.slice(0, rest.length - periodMatch[0].length)
  }

  const segments = rest.split('.').filter((s) => s.length > 0)
  const head = segments.shift()
  if (!head) throw new RhythmParseError(`keine Klasse in "${raw}"`)

  let colours: RhythmColour[] = []
  for (const seg of segments) {
    if (!COLOUR_RE.test(seg)) throw new RhythmParseError(`unerwarteter Teil "${seg}" in "${raw}"`)
    colours = colours.concat(splitColours(seg))
  }

  const m = head.match(CLASS_RE)
  if (!m) throw new RhythmParseError(`Klasse nicht erkannt: "${head}" in "${raw}"`)
  const cls = m[1] as RhythmClass
  const groupToken = m[2]
  const plus = m[3] as LightCharacter['plus'] | undefined
  const trailing = m[4] ?? ''
  if (trailing) {
    if (!COLOUR_RE.test(trailing)) throw new RhythmParseError(`unerwarteter Zusatz "${trailing}" in "${raw}"`)
    colours = colours.concat(splitColours(trailing))
  }

  let groups: number[] = []
  let morse: string | undefined
  if (groupToken) {
    if (cls === 'Mo') {
      morse = groupToken
      if (!MORSE[morse]) throw new RhythmParseError(`unbekannter Morsebuchstabe "${morse}"`)
    } else {
      groups = groupToken.split('+').map((n) => {
        const v = Number(n)
        if (!Number.isInteger(v) || v <= 0) throw new RhythmParseError(`ungültige Gruppe "${n}" in "${raw}"`)
        return v
      })
    }
  }

  const sectored = colours.length > 1 && cls !== 'Al'
  const phases = buildPhases({ cls, groups, plus, morse, colours, periodS, raw })
  const built = total(phases)
  if (periodS === 0) periodS = built
  const character: LightCharacter = {
    raw: text,
    cls,
    groups,
    plus,
    colours,
    sectored,
    morse,
    periodS,
    phases,
  }
  return character
}

interface BuildInput {
  cls: RhythmClass
  groups: number[]
  plus?: 'LFl' | 'Fl' | 'Q'
  morse?: string
  colours: RhythmColour[]
  periodS: number
  raw: string
}

function fill(phases: Phase[], periodS: number, raw: string): Phase[] {
  const used = total(phases)
  const remainder = periodS - used
  if (periodS === 0) return phases
  if (remainder < -1e-9) {
    throw new RhythmParseError(
      `"${raw}": die Kennung braucht ${used.toFixed(2)} s, die Wiederkehr ist aber nur ${periodS} s`,
    )
  }
  if (remainder > 1e-9) phases.push({ duration: remainder, intensity: 0 })
  return phases
}

function buildPhases(input: BuildInput): Phase[] {
  const { cls, groups, plus, morse, periodS, raw } = input
  switch (cls) {
    case 'F':
      return [{ duration: periodS || 1, intensity: 1 }]
    case 'Iso': {
      if (!periodS) throw new RhythmParseError(`Iso braucht eine Wiederkehr: "${raw}"`)
      const half = periodS / 2
      return [
        { duration: half, intensity: 1 },
        { duration: half, intensity: 0 },
      ]
    }
    case 'Oc': {
      if (!periodS) throw new RhythmParseError(`Oc braucht eine Wiederkehr: "${raw}"`)
      const count = groups[0] ?? 1
      const dark = TIMING.eclipse * count
      const light = periodS - dark
      if (light <= 0) throw new RhythmParseError(`"${raw}": Verdunkelungen passen nicht in die Wiederkehr`)
      const perLight = light / count
      const phases: Phase[] = []
      for (let i = 0; i < count; i += 1) {
        phases.push({ duration: perLight, intensity: 1 })
        phases.push({ duration: TIMING.eclipse, intensity: 0 })
      }
      return phases
    }
    case 'Al': {
      if (!periodS) throw new RhythmParseError(`Al braucht eine Wiederkehr: "${raw}"`)
      const cols = input.colours.length ? input.colours : (['W'] as RhythmColour[])
      const each = periodS / cols.length
      const gap = Math.min(TIMING.alternateGap, each / 2)
      const phases: Phase[] = []
      for (const colour of cols) {
        phases.push({ duration: each - gap, intensity: 1, colour })
        phases.push({ duration: gap, intensity: 0 })
      }
      return phases
    }
    case 'Mo': {
      if (!morse) throw new RhythmParseError(`Mo braucht einen Buchstaben: "${raw}"`)
      const code = MORSE[morse]!
      const phases: Phase[] = []
      for (const [i, sym] of [...code].entries()) {
        phases.push({ duration: sym === '.' ? TIMING.morseDot : TIMING.morseDash, intensity: 1 })
        if (i < code.length - 1) phases.push({ duration: TIMING.morseGap, intensity: 0 })
      }
      return fill(phases, periodS, raw)
    }
    case 'FFl': {
      const count = groups[0] ?? 1
      const flashes = groupPhases('Fl', count)
      const phases = flashes.map((p) => ({ ...p, intensity: p.intensity === 0 ? TIMING.fixedBaseline : 1 }))
      const used = total(phases)
      const remainder = (periodS || used) - used
      if (remainder > 1e-9) phases.push({ duration: remainder, intensity: TIMING.fixedBaseline })
      return phases
    }
    case 'Q':
    case 'VQ':
    case 'UQ':
    case 'IQ':
    case 'IVQ':
    case 'IUQ': {
      const cycle =
        cls === 'Q' || cls === 'IQ'
          ? TIMING.quickCycle
          : cls === 'VQ' || cls === 'IVQ'
            ? TIMING.veryQuickCycle
            : TIMING.ultraQuickCycle
      if (groups.length === 0) {
        // Durchgehendes Funkeln: ein Takt ist die Wiederkehr.
        const n = periodS ? Math.max(1, Math.round(periodS / cycle)) : 1
        return groupPhases(cls, n)
      }
      const phases: Phase[] = []
      for (const [i, count] of groups.entries()) {
        if (i > 0) phases.push({ duration: TIMING.subGroupGap, intensity: 0 })
        phases.push(...groupPhases(cls, count))
      }
      if (plus) {
        phases.push({ duration: TIMING.plusGap, intensity: 0 })
        phases.push(...groupPhases(plus, 1))
      }
      return fill(phases, periodS, raw)
    }
    case 'Fl':
    case 'LFl': {
      const counts = groups.length ? groups : [1]
      const phases: Phase[] = []
      for (const [i, count] of counts.entries()) {
        if (i > 0) phases.push({ duration: TIMING.subGroupGap, intensity: 0 })
        phases.push(...groupPhases(cls, count))
      }
      if (plus) {
        phases.push({ duration: TIMING.plusGap, intensity: 0 })
        phases.push(...groupPhases(plus, 1))
      }
      return fill(phases, periodS, raw)
    }
    default:
      throw new RhythmParseError(`Klasse ${cls} nicht unterstützt`)
  }
}

export interface RhythmSample {
  intensity: number
  colour?: RhythmColour
}

/** Zustand der Kennung zum Zeitpunkt t (Sekunden, läuft zyklisch). */
export function sampleAt(character: LightCharacter, t: number): RhythmSample {
  const period = character.periodS || total(character.phases)
  if (period <= 0) return { intensity: 1, colour: character.colours[0] }
  let x = t % period
  if (x < 0) x += period
  for (const phase of character.phases) {
    if (x < phase.duration) {
      return {
        intensity: phase.intensity,
        colour: phase.colour ?? (character.sectored ? undefined : character.colours[0]),
      }
    }
    x -= phase.duration
  }
  const last = character.phases[character.phases.length - 1]
  return { intensity: last?.intensity ?? 0, colour: last?.colour }
}

/** Zahl der Blitze je Wiederkehr, das ist die Zahl, die der Lernende zählt. */
export function flashCount(character: LightCharacter): number {
  return character.phases.filter((p) => p.intensity >= 1).length
}

const COLOUR_NAMES: Record<RhythmColour, { en: string; de: string }> = {
  W: { en: 'white', de: 'weiß' },
  R: { en: 'red', de: 'rot' },
  G: { en: 'green', de: 'grün' },
  Y: { en: 'yellow', de: 'gelb' },
  Bu: { en: 'blue', de: 'blau' },
  Vi: { en: 'violet', de: 'violett' },
}

const CLASS_NAMES: Record<RhythmClass, { en: string; de: string }> = {
  F: { en: 'fixed', de: 'Festfeuer' },
  Fl: { en: 'flashing', de: 'Blitzfeuer' },
  LFl: { en: 'long flashing', de: 'langes Blitzfeuer' },
  FFl: { en: 'fixed and flashing', de: 'Fest- und Blitzfeuer' },
  Q: { en: 'quick', de: 'Funkelfeuer' },
  VQ: { en: 'very quick', de: 'schnelles Funkelfeuer' },
  UQ: { en: 'ultra quick', de: 'sehr schnelles Funkelfeuer' },
  IQ: { en: 'interrupted quick', de: 'unterbrochenes Funkelfeuer' },
  IVQ: { en: 'interrupted very quick', de: 'unterbrochenes schnelles Funkelfeuer' },
  IUQ: { en: 'interrupted ultra quick', de: 'unterbrochenes sehr schnelles Funkelfeuer' },
  Iso: { en: 'isophase', de: 'Gleichtaktfeuer' },
  Oc: { en: 'occulting', de: 'Unterbrochenes Feuer' },
  Mo: { en: 'morse', de: 'Morsefeuer' },
  Al: { en: 'alternating', de: 'Wechselfeuer' },
}

/** Klartext der Kennung in beiden Sprachen, so wie ein Ausbilder sie vorliest. */
export function describeCharacter(character: LightCharacter): { en: string; de: string } {
  const cls = CLASS_NAMES[character.cls]
  const cols = character.colours.map((c) => COLOUR_NAMES[c])
  const group = character.groups.length ? ` group ${character.groups.join('+')}` : ''
  const groupDe = character.groups.length ? `, Gruppe ${character.groups.join('+')}` : ''
  const plusEn = character.plus ? ` plus one ${CLASS_NAMES[character.plus].en} flash` : ''
  const plusDe = character.plus ? ` plus ein ${CLASS_NAMES[character.plus].de}` : ''
  const morseEn = character.morse ? ` letter ${character.morse}` : ''
  const morseDe = character.morse ? ` Buchstabe ${character.morse}` : ''
  const colEn = cols.length ? ` ${cols.map((c) => c.en).join('/')}` : ''
  const colDe = cols.length ? ` ${cols.map((c) => c.de).join('/')}` : ''
  const showPeriod = character.periodS > 0 && character.cls !== 'F'
  const perEn = showPeriod ? `, period ${character.periodS} s` : ''
  const perDe = showPeriod ? `, Wiederkehr ${character.periodS} s` : ''
  return {
    en: `${cls.en}${group}${plusEn}${morseEn}${colEn}${perEn}`.trim(),
    de: `${cls.de}${groupDe}${plusDe}${morseDe}${colDe}${perDe}`.trim(),
  }
}
